// patch 224: My money plan. The numbers and the maths.
// Saved on your Worthly account as money_diary.plan. Everything Worthly knows
// (funds, investments, debts, income, spending) is read live from your account,
// so it updates on its own. Anything you type in is kept in the plan.
import { getFundBalance } from '../../utils/fundMath';
import { monthTotals, monthKeyOf, shiftMonthKey, categoryLabel } from '../../utils/monthEndReview';
import { investmentBalance } from "../../utils/investMath"; // dark_13: one sum for investment balances

export const num = (v) => { const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.\-]/g, '')); return isFinite(n) ? n : 0; };
export const fmt = (v) => (v < 0 ? '−' : '') + '$' + Math.round(Math.abs(v)).toLocaleString('en-CA');
export const fmtC = (v) => (v < 0 ? '−' : '') + '$' + Math.abs(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const short = (v) => (Math.abs(v) >= 1e6 ? (v < 0 ? '−' : '') + '$' + (Math.abs(v) / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M' : Math.abs(v) >= 1e5 ? fmt(Math.round(v / 1000) * 1000) : fmt(v));
export const withCommas = (s) => {
  const clean = String(s == null ? '' : s).replace(/[^0-9.]/g, '');
  if (!clean) return '';
  const [i, ...rest] = clean.split('.');
  const int = i ? Number(i).toLocaleString('en-CA') : '0';
  return rest.length ? int + '.' + rest.join('').slice(0, 2) : int;
};
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
export const monthYear = (d) => MON[d.getMonth()] + ' ' + d.getFullYear();
export const dateLabel = (iso) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : MON[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear(); };
export const pid = (p = 'mp') => p + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

// ---------------------------------------------------------------- the steps
export const CHAPTERS = [
  { n: 1, name: 'About you', e: '🌷', bg: 'peach', ink: 'peachInk', sub: "Your life, what's ahead and what matters" },
  { n: 2, name: 'Where you are', e: '📍', bg: 'sky', ink: 'skyInk', sub: 'What you own, owe, earn and spend' },
  { n: 3, name: 'Your safety net', e: '🛟', bg: 'mint', ink: 'mintInk', sub: 'Savings cushion, insurance and paperwork' },
  { n: 4, name: 'Your goals', e: '🎯', bg: 'blush', ink: 'blushInk', sub: "What you're saving for, family travel and what comes first" },
  { n: 5, name: 'Growing it', e: '🌱', bg: 'butter', ink: 'butterInk', sub: 'Investing, tax, moving abroad and retirement' },
];
export const STEPS = [
  { k: 'life', ch: 1, t: 'Your life right now' },
  { k: 'ahead', ch: 1, t: "What's ahead" },
  { k: 'values', ch: 1, t: 'What matters to you' },
  { k: 'networth', ch: 2, t: 'What you own and owe' },
  { k: 'money', ch: 2, t: 'Each month' },
  { k: 'extras', ch: 2, t: 'Your extras' },
  { k: 'debt', ch: 2, t: 'What you owe' },
  { k: 'cushion', ch: 3, t: 'Your cushion' },
  { k: 'insurance', ch: 3, t: 'Insurance check' },
  { k: 'paper', ch: 3, t: 'Grown-up paperwork' },
  { k: 'goals', ch: 4, t: 'Your goals' },
  { k: 'move', ch: 4, t: 'Moving abroad' },
  { k: 'travel', ch: 4, t: 'Seeing family' },
  { k: 'order', ch: 4, t: 'How it adds up' },
  { k: 'invest', ch: 5, t: 'How you invest' },
  { k: 'tax', ch: 5, t: 'Tax' },
  { k: 'leaving', ch: 5, t: 'Your money when you move' },
  { k: 'retire', ch: 5, t: 'Retirement' },
  { k: 'calendar', ch: 5, t: 'Your money calendar' }, // up_01
  { k: 'guesses', ch: 5, t: 'Your guesses' }, // up_01
];
export const stepIndex = (k) => STEPS.findIndex((s) => s.k === k);

// ---------------------------------------------------------------- a new plan
export const INSURANCE = [
  { k: 'life', e: '💗', t: 'Life insurance', s: 'Usually 1 to 2 times your pay through work', opts: ['Through work', 'My own', 'Not sure', "Don't have"] },
  { k: 'disability', e: '🩹', t: 'Disability', s: "Pays you if you can't work for a while. This one matters most at your age!", opts: ['Through work', 'My own', 'Not sure', "Don't have"] },
  { k: 'health', e: '🦷', t: 'Health and dental', s: '', opts: ['Through work', 'My own', 'Not sure'] },
  { k: 'tenant', e: '🏠', t: 'Tenant insurance', s: '', opts: ['Have it', 'Not sure', "Don't have"] },
  { k: 'car', e: '🚗', t: 'Car insurance', s: '', opts: ['Have it', "Don't have a car"] },
  { k: 'critical', e: '🌿', t: 'Critical illness', s: 'A lump sum for a serious illness, often a work add-on', opts: ['Have it', 'Not sure', "Don't have"] },
];
export const PAPER = [
  { k: 'will', t: 'A will', s: "In Ontario, a partner you're not married to doesn't inherit automatically without one" },
  { k: 'poaMoney', t: 'Power of attorney for money', s: "Someone to handle your money if you can't" },
  { k: 'poaCare', t: 'Power of attorney for personal care', s: 'Ontario has two separate ones' },
  { k: 'benef', t: 'Beneficiaries', s: 'On your RRSP, work pension and work savings plan' },
  { k: 'together', t: 'Living together agreement', s: 'Optional, but useful when you share some money' },
  { k: 'where', t: 'Where everything lives', s: 'An account list someone you trust can find' },
];
export const ACCOUNTS = [
  { k: 'rrsp', e: '🌳', t: 'RRSP', s: 'Your main one, with refunds going to sinking funds', opts: ['Using', 'Worth a look', 'Not using', 'Later'] },
  { k: 'pension', e: '🏢', t: 'Work pension', s: 'Moves to a locked-in account if you leave your job', opts: ['Using', 'Not using'] },
  { k: 'group', e: '🪴', t: 'Work savings plan', s: 'Planning to move it into your RRSP', opts: ['Using', 'To do', 'Not using'] },
  { k: 'fhsa', e: '🏡', t: 'FHSA', s: 'For a first home in Canada. If you never buy, it can roll into your RRSP without using RRSP room!', opts: ['Using', 'Worth a look', 'Not using'] },
  { k: 'tfsa', e: '🌤️', t: 'TFSA', s: "France and the UK don't treat it as tax-free, which is why you chose your RRSP", opts: ['Using', 'Not using', 'Worth a look'] },
  { k: 'resp', e: '🎓', t: 'RESP', s: 'Once a baby arrives. The government adds 20%, up to $500 a year!', opts: ['Using', 'Later', 'Not using'] },
];
export const LEAVING = [
  { e: '🌳', t: 'RRSP', s: 'You can keep it. Withdrawals have 25% held back for Canadian tax.' },
  { e: '🏢', t: 'Work pension', s: 'Moves to a locked-in account you can keep.' },
  { e: '💼', t: 'Worthly HQ', s: "Your shares count for Canada's departure tax, so decide what happens to the company before you go." },
  { e: '🏦', t: 'Regular savings', s: "No tax just for leaving. Keep a Canadian account open for a while." },
  { e: '🎓', t: 'RESP', s: "No new government top-ups once you're not a resident." },
];
// up_02: your own lists. Until you change one, it's the list above.
export const insOf = (plan) => (plan && Array.isArray(plan.insList) ? plan.insList : INSURANCE);
export const paperOf = (plan) => (plan && Array.isArray(plan.paperList) ? plan.paperList : PAPER);
export const accOf = (plan) => (plan && Array.isArray(plan.accList) ? plan.accList : ACCOUNTS);
export const leavingOf = (plan) => (plan && Array.isArray(plan.leavingNotes) ? plan.leavingNotes : LEAVING.map((x, i) => ({ id: 'lv' + i, ...x })));
// up_03: reminders that show up in your next steps for a few months each year.
// for: 0 means it stays until you tick it off.
const REMIND_START = (plan) => [
  { id: 'pension-mix', t: 'Change your pension mix to about 7.5% Canada', s: 'Investing · Sun Life', from: 1, for: 0 },
  { id: 'sunlife', t: 'Check your December Sun Life statement', s: 'Investing · for a full-year figure', from: 12, for: 3 },
  { id: 'stub', t: 'Check a pay stub shows no CPP or EI', s: 'Before your savings step up', from: ((Math.round(num(plan.money.stepMonth)) || 8) + 10) % 12 + 1, for: 2 },
];
export const remindersOf = (plan) => (plan && Array.isArray(plan.reminders) ? plan.reminders : REMIND_START(plan));
export const CHANGES = [
  { k: 'job', e: '💼', t: 'New job', steps: ['money', 'extras', 'insurance', 'invest', 'goals'] },
  { k: 'pay', e: '💸', t: 'Raise or bonus change', steps: ['money', 'extras'] },
  { k: 'baby', e: '🍼', t: 'Baby on the way', steps: ['goals', 'insurance', 'paper', 'travel'] },
  { k: 'married', e: '💍', t: 'Engaged or married', steps: ['life', 'paper', 'goals'] },
  { k: 'move', e: '✈️', t: 'Move date changed', steps: ['ahead', 'move', 'travel', 'retire'] },
  { k: 'home', e: '🏡', t: 'Bought a home', steps: ['networth', 'goals', 'money'] },
  { k: 'car', e: '🚗', t: 'Car paid off', steps: ['debt', 'money'] },
  { k: 'none', e: '🌸', t: 'Nothing big', steps: [] },
];

const isCar = (n) => /car|auto|mazda|vehicle/i.test(n || '');

export function newPlan(raw, now = new Date()) {
  const y = now.getFullYear();
  const ret = raw && raw.retire;
  return {
    v: 1,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    stepAt: 0,
    finished: false,
    life: { age: (ret && num(ret.age)) || 27, ageYear: y, city: 'Toronto, Ontario', livingWith: 'Enzo', together: 'Some shared', pay: ['Every two weeks', 'Quarterly bonus'] },
    ahead: [
      { id: 'carpaid', e: '🚗', t: 'Car paid off', when: 'Dec 2028', s: 'Frees up your car payment' },
      { id: 'baby', e: '🍼', t: 'First baby', when: 'Around 2030 to 2032', s: 'Early 30s, once things feel stable' },
      { id: 'move', e: '✈️', t: 'Moving to Europe', when: 'Ready by 2031 · likely 2034 to 2036', s: "Planning for the earliest date means you're ready either way" },
      { id: 'family', e: '🇳🇬', t: 'Seeing family', when: 'Every year, for life', s: "Nigeria each December now, then every season once you're in Europe" },
      { id: 'retire', e: '🌻', t: 'Retirement', when: 'Around 63', s: 'Somewhere between Europe and Nigeria' },
    ],
    extraAhead: [],
    values: { protect: 'Seeing family', success: 'Moving to Europe with a cushion, seeing my family often and no money worries!' },
    nw: { inv: {}, manual: [
      { id: 'pension', e: '🏢', t: 'Work pension', s: 'Sun Life · locked in', kind: 'invest', amount: 24805 },
      { id: 'group', e: '🪴', t: 'Work savings plan', s: 'Sun Life · moving to your RRSP', kind: 'invest', amount: 0 },
    ] },
    // patch 225: your pay covers each month; extras (bonuses, refund, third paycheques) fill sinking funds.
    money: {
      payA: 6088, payB: 6775, stepMonth: 8, raiseMonth: 4, raise: 3,
      rrspA: 700, rrspB: 1000, efA: 700, efB: 1000,
      pension: 420, pensionYou: 0,
      essentials: [
        { id: 'e1', e: '🏠', t: 'Rent', amount: 1880 },
        { id: 'e2', e: '🛒', t: 'Groceries', amount: 550 },
        { id: 'e3', e: '🚗', t: 'Car loan', amount: 546 },
        { id: 'e4', e: '📱', t: 'Phone and internet', amount: 130 },
        { id: 'e5', e: '💡', t: 'Utilities', amount: 75 },
        { id: 'e6', e: '⛽', t: 'Gas', amount: 180 },
        { id: 'e7', e: '🧺', t: 'Other essentials', amount: 150 },
      ],
      buffer: 650,
      months: 6,
    },
    extras: [
      { id: 'x1', e: '🎉', t: 'Quarterly bonuses', when: [1, 4, 7, 10], amount: 7877 },
      { id: 'x2', e: '🎉', t: 'Yearly bonus', when: [3], amount: 4200 },
      { id: 'x3', e: '💸', t: 'Tax refund', when: [5], amount: 3837 },
      { id: 'x4', e: '💵', t: 'Third paycheques', when: [4, 10], amount: 6775 },
    ],
    sinking: [
      { id: 's1', e: '✈️', t: 'Travel/Vacation', s: 'Seeing family and one trip', kind: 'travel', amount: 6000 },
      { id: 's2', e: '☂️', t: 'Car Protection', s: 'Insurance', amount: 6500 },
      { id: 's3', e: '💗', t: 'Car Safety', s: 'Maintenance', amount: 2000 },
      { id: 's4', e: '🎁', t: 'Gifting', amount: 1500 },
      { id: 's5', e: '🌿', t: 'Breathing Room', s: 'Once, to build it up', amount: 1000, once: true },
    ],
    rrspShare: 50,
    debts: {}, // by Worthly debt id: { rate, pay, every, ends }
    debtChoice: 'Keep it as it is',
    cushion: { fundId: null, target: 15000, cats: null, purpose: 'Real emergencies, with a $3,000 buffer on top of $12,000. Once it is full, your pay savings go to your future fund.', comfy: '' },
    insurance: { life: 'Through work', disability: 'Not sure', health: 'Through work', tenant: 'Have it', car: 'Have it', critical: 'Not sure' },
    rely: 'Not yet',
    paper: { benef: true, where: true },
    goals: [
      { id: 'home', e: '🏡', t: 'A home', tier: 'love', amount: 0, year: '', s: 'Still deciding: buy, or rent and invest' },
    ],
    baby: { on: true, year: 2030, months: 12, ei: 3160, topWeeks: 20, topUp: null, fundId: null, saved: 0, monthly: 0, tier: 'must' },
    resp: { on: true, kids: 1, yearly: 2500, tier: 'love' },
    // patch 227: the move is paid from your future fund (a savings account), not investments
    move: {
      ready: 2032, readyMonth: 3, likely: '2034 to 2036', tier: 'must',
      costs: [{ id: 'c1', e: '✈️', t: 'Your half of the move', amount: 34279 }],
      europe: {
        shared: [
          { id: 'eu1', e: '🏠', t: 'Rent, one-bed Paris or London', amount: 2400 },
          { id: 'eu2', e: '💡', t: 'Utilities', amount: 150 },
          { id: 'eu3', e: '📶', t: 'Internet', amount: 50 },
          { id: 'eu4', e: '🛒', t: 'Groceries', amount: 900 },
        ],
        personal: [
          { id: 'eu5', e: '📱', t: 'Phone', amount: 80 },
          { id: 'eu6', e: '🚇', t: 'Transit pass', amount: 110 },
          { id: 'eu7', e: '🧺', t: 'Personal and misc', amount: 700 },
        ],
        months: 6,
      },
      fundId: null, saved: 0,
      from: ['Future fund'],
    },
    travel: {
      now: [{ id: 't1', t: 'Nigeria every December', amount: 3000 }, { id: 't2', t: 'One vacation', amount: 3000 }],
      later: [{ id: 't3', t: 'Nigeria every season', trips: 4, amount: 1700 }, { id: 't4', t: 'One vacation', trips: 1, amount: 3000 }],
      laterFrom: 2031, kidTicket: 0, kidsFrom: '', used: [],
    },
    order: ['funds', 'efund', 'move', 'baby', 'retire', 'resp', 'home'],
    invest: { mix: '25% VEQT · 75% VXC', why: "About 7.5% Canada, so your money follows the currency you'll spend in Europe.", drop: 'Keep going as normal', accounts: { rrsp: 'Using', pension: 'Using', group: 'To do', fhsa: 'Worth a look', tfsa: 'Not using', resp: 'Later' } },
    tax: { refund: 'Sinking funds', payself: 'Not yet' },
    leavingList: [
      { id: 'l1', t: 'Chat with a cross-border tax advisor', done: false },
      { id: 'l2', t: 'Decide what to withdraw and when', done: false },
      { id: 'l3', t: "Plan Worthly HQ's next step", done: false },
      { id: 'l4', t: 'Line up a bank account in your new country', done: false },
    ],
    retire: { age: 63, feel: 'Comfortable, like now', everyday: 45000, gov: 15000 },
    assume: { prices: 2.5, growth: 6, retGrowth: 4.5, until: 100 },
    done: {},
    extraTodos: [],
    checkups: [],
    past: [],
  };
}

// Fills in anything an older saved plan is missing.
export function readPlan(saved, raw) {
  if (!saved || typeof saved !== 'object' || !saved.v) return null;
  const base = newPlan(raw, new Date(saved.createdAt || Date.now()));
  const out = { ...base, ...saved };
  ['life', 'values', 'nw', 'money', 'cushion', 'insurance', 'paper', 'baby', 'resp', 'move', 'travel', 'invest', 'tax', 'retire', 'assume', 'done', 'debts'].forEach((k) => {
    out[k] = { ...base[k], ...(saved[k] || {}) };
  });
  out.invest.accounts = { ...base.invest.accounts, ...((saved.invest && saved.invest.accounts) || {}) };
  // patch 225: new pieces, and the three nice-to-haves taken out
  if (!Array.isArray(out.money.essentials)) out.money.essentials = base.money.essentials;
  if (!Array.isArray(saved.extras)) out.extras = base.extras;
  if (!Array.isArray(saved.sinking)) out.sinking = base.sinking;
  if (!Array.isArray(out.travel.used)) out.travel.used = [];
  if (!saved.move || !saved.move.europe) {
    out.move = { ...base.move, tier: (saved.move && saved.move.tier) || 'must', likely: (saved.move && saved.move.likely) || base.move.likely };
    out.cushion = { ...out.cushion, target: 15000, purpose: base.cushion.purpose };
  }
  const gone = ['donna', 'kobo', 'car2'];
  out.goals = (out.goals || []).filter((g) => !gone.includes(g.id));
  out.order = (out.order || []).filter((k) => !gone.includes(k));
  if (saved.money && saved.money.payA == null) {
    ['pay', 'bonus', 'bonusCount', 'funds', 'fun', 'bonusGoes'].forEach((k) => { delete out.money[k]; });
    if (!num(out.money.pension)) out.money.pension = base.money.pension;
  }
  if (!saved.baby || saved.baby.topWeeks == null) out.baby = { ...out.baby, ei: base.baby.ei, topWeeks: 20, topUp: null, monthly: num(out.baby.monthly) };
  // ln_31: the new future fund plan (a $40,000 target and the move broken down), put in once
  if (!out.ff40) {
    out.move = { ...out.move, target: 40000, costs: [
      { id: 'mc1', e: '✈️', t: 'Flights, one way with bags', amount: 1250 },
      { id: 'mc2', e: '🚢', t: 'Shipping, 20ft container', amount: 5000 },
      { id: 'mc3', e: '🏨', t: 'Temporary stay while flat hunting, 4 weeks', amount: 2000 },
      { id: 'mc4', e: '🔑', t: 'Rental deposit and agency fees', amount: 4000 },
      { id: 'mc5', e: '🏠', t: "First month's rent", amount: 1300 },
      { id: 'mc6', e: '🛂', t: 'Visa, immigration and legal', amount: 1500 },
      { id: 'mc7', e: '🛋️', t: 'Furnishing gaps and appliances', amount: 2500 },
      { id: 'mc8', e: '📱', t: 'Admin, phones and bank setup', amount: 1000 },
      { id: 'mc9', e: '🧡', t: 'Buffer', amount: 1500 },
    ] };
    out.money = { ...out.money, queue: ['efund', 'move'] };
    out.ff40 = true;
  }
  if (!Array.isArray(out.money.queue)) out.money.queue = ['efund', 'move'];
  // ln_33: money that frees up later and joins your goal savings, like your car loan once it's paid off
  if (!Array.isArray(out.money.boosts)) out.money.boosts = [{ id: 'bcar', e: '🚗', t: 'Car loan, once it\'s paid off', amount: 591, start: '2029-01' }];
  return out;
}

// ---------------------------------------------------------------- Worthly numbers
// Same rules Worthly uses for balances and net worth, so the numbers always match.
export function worthly(data, plan, now = new Date()) {
  const tx = (data && data.transactions) || [];
  const mk = monthKeyOf(now);
  const funds = ((data && data.sinking_funds) || []).map((f) => ({
    id: String(f.id), name: f.name || 'Fund', emoji: f.emoji || '💰', goal: num(f.goal),
    balance: getFundBalance({ fund: f, transactions: tx }),
    counted: f.exclude_from_net_worth !== true,
  }));
  const invs = ((data && data.investment_accounts) || []).map((a) => {
    const id = String(a.id);
    const bal = investmentBalance(a, tx, mk, { all: true }); // dark_13
    return { id, name: a.name || 'Investments', balance: bal };
  });
  const assets = ((data && data.assets) || []).map((a) => {
    const cbm = a.current_by_month || {};
    let v;
    if (cbm[mk] != null) v = num(cbm[mk]);
    else { const ks = Object.keys(cbm).sort().filter((k) => k <= mk); v = ks.length ? num(cbm[ks[ks.length - 1]]) : num(a.starting_balance); }
    return { id: String(a.id), name: a.name || 'Asset', balance: v };
  });
  const debts = ((data && data.debts) || []).map((d) => {
    let start;
    if (d.starting_balance != null) start = num(d.starting_balance);
    else { const cbm = d.current_by_month || {}; const ks = Object.keys(cbm).sort(); start = ks.length ? num(cbm[ks[ks.length - 1]]) : num(d.original_amount); }
    const paid = tx.filter((t) => t.is_debt_payment && String(t.debt_id) === String(d.id)).reduce((s, t) => s + num(t.amount), 0);
    return { id: String(d.id), name: d.name || 'Debt', balance: Math.max(0, start - paid), original: num(d.original_amount) };
  });
  // Everyday cash, the way Worthly's Net Worth tab works it out.
  const bs = (data && data.budget_settings) || {};
  const leftover = num((bs.leftover_by_month || {})[mk]);
  const mt = tx.filter((t) => t.month_key === mk);
  const cash = leftover
    + mt.filter((t) => t.is_income && !t.is_from_savings && !t.is_interest).reduce((s, t) => s + num(t.amount), 0)
    + mt.filter((t) => t.is_savings && t.transaction_type === 'withdrawal').reduce((s, t) => s + num(t.amount), 0)
    - mt.filter((t) => !t.is_income && !t.is_savings).reduce((s, t) => s + num(t.amount), 0)
    - mt.filter((t) => t.is_savings && t.transaction_type !== 'withdrawal').reduce((s, t) => s + num(t.amount), 0);

  // Averages over the last few full months that have something in them.
  const N = Math.max(1, Math.min(24, num(plan && plan.money && plan.money.months) || 6));
  let months = 0, income = 0, spent = 0;
  const cats = {};
  const fundDeps = {};
  for (let k = 1; k <= 24 && months < N; k++) {
    const key = shiftMonthKey(mk, -k);
    const t = monthTotals(tx, key);
    if (!t.tx.length) continue;
    months += 1; income += t.income; spent += t.spent;
    t.expenses.forEach((e) => { const c = e.category || 'Other'; cats[c] = (cats[c] || 0) + num(e.amount); });
    t.tx.filter((e) => e.account_type === 'fund' && e.transaction_type === 'deposit').forEach((e) => { const id = String(e.account_id); fundDeps[id] = (fundDeps[id] || 0) + num(e.amount); });
  }
  const avg = (n) => (months ? n / months : 0);
  // patch 225: money taken out of each fund this calendar year
  const yr = String(now.getFullYear());
  const fundOut = {};
  tx.filter((t) => t.account_type === 'fund' && t.transaction_type === 'withdrawal' && String(t.date || t.month_key || '').slice(0, 4) === yr).forEach((t) => {
    const id = String(t.account_id);
    (fundOut[id] = fundOut[id] || []).push({ id: 'w' + t.id, t: t.description || t.name || t.note || 'From your fund', amount: num(t.amount), date: String(t.date || '').slice(0, 10) });
  });
  const categories = Object.keys(cats).map((c) => { const L = categoryLabel(c); return { key: c, emoji: L.emoji, name: L.name, monthly: avg(cats[c]) }; }).sort((a, b) => b.monthly - a.monthly);
  Object.keys(fundDeps).forEach((k) => { fundDeps[k] = avg(fundDeps[k]); });
  return { funds, invs, assets, debts, cash, months, income: avg(income), spent: avg(spent), categories, fundDeps, fundOut };
}

// ---------------------------------------------------------------- everything worked out
// A goal's status from what it needs and what's going in.
const statusOf = (have, need, monthly, monthlyNeed) => {
  if (need > 0 && have >= need) return 'done';
  if (monthly <= 0 && have <= 0) return 'notyet';
  return monthly + 0.5 >= monthlyNeed ? 'on' : 'behind';
};
// Worthly trial: an optional month (1-12) counts to that month of the year.
const monthsUntil = (year, now, month) => Math.max(1, (num(year) - now.getFullYear()) * 12 + (num(month) ? num(month) - 1 : 0) - now.getMonth());

export function work(plan, W, now = new Date()) {
  const y = now.getFullYear();
  const A = plan.assume;
  const P = num(A.prices) / 100, G = num(A.growth) / 100, GR = num(A.retGrowth) / 100;
  const real = (1 + G) / (1 + P) - 1, realRet = (1 + GR) / (1 + P) - 1;
  const age = num(plan.life.age) + (y - (num(plan.life.ageYear) || y));
  const fv = (P0, c, r, n) => P0 * Math.pow(1 + r, n) + (Math.abs(r) < 1e-9 ? c * n : c * (Math.pow(1 + r, n) - 1) / r);

  // Net worth
  const invCounted = W.invs.filter((a) => (plan.nw.inv[a.id] || {}).count !== false);
  const invW = invCounted.reduce((s, a) => s + a.balance, 0);
  const manual = plan.nw.manual || [];
  const manInv = manual.filter((m) => m.kind === 'invest').reduce((s, m) => s + num(m.amount), 0);
  const manCash = manual.filter((m) => m.kind === 'cash').reduce((s, m) => s + num(m.amount), 0);
  const manDebt = manual.filter((m) => m.kind === 'debt').reduce((s, m) => s + num(m.amount), 0);
  const fundsCounted = W.funds.filter((f) => f.counted).reduce((s, f) => s + f.balance, 0);
  const assets = W.assets.reduce((s, a) => s + a.balance, 0);
  const debtW = W.debts.reduce((s, d) => s + d.balance, 0);
  const invested = invW + manInv;
  const netWorth = fundsCounted + W.cash + assets + invested + manCash - debtW - manDebt;

  // Each month, from your pay
  const M = plan.money;
  const step = Math.min(12, Math.max(1, Math.round(num(M.stepMonth)) || 8));
  const aMonths = step - 1, bMonths = 13 - step;
  const payA = num(M.payA), payB = num(M.payB);
  const payAvg = (aMonths * payA + bMonths * payB) / 12;
  const essList = Array.isArray(M.essentials) ? M.essentials : [];
  const essMonth = essList.reduce((s, e) => s + num(e.amount), 0);
  const buffer = num(M.buffer);
  const emergencyMonth = essMonth + buffer;
  const youPension = num(M.pensionYou);
  const leftA = payA - essMonth - num(M.rrspA) - num(M.efA) - youPension;
  const leftB = payB - essMonth - num(M.rrspB) - num(M.efB) - youPension;
  const rrspYear = aMonths * num(M.rrspA) + bMonths * num(M.rrspB);
  const efYear = aMonths * num(M.efA) + bMonths * num(M.efB);
  const pensionYear = 12 * (num(M.pension) + youPension);
  const colgateYear = 12 * num(M.pension);

  // Seeing family (needed for the travel sinking fund)
  const TR = plan.travel;
  const travelNowYear = (TR.now || []).reduce((s, t) => s + num(t.amount), 0);
  const travelGoal = num(TR.target) > 0 ? num(TR.target) : travelNowYear; // plan_35: your own amount, if you set one
  const travelLaterYear = (TR.later || []).reduce((s, t) => s + num(t.amount) * (num(t.trips) || 1), 0);

  // Your extras each year
  const extras = Array.isArray(plan.extras) ? plan.extras : [];
  const extrasIn = extras.reduce((s, x) => s + num(x.amount), 0);
  const sinking = (Array.isArray(plan.sinking) ? plan.sinking : []).map((f) => ({ ...f, amount: f.kind === 'travel' ? travelNowYear : num(f.amount) }));
  const fundsYear = sinking.reduce((s, f) => s + f.amount, 0);
  const extrasLeft = extrasIn - fundsYear;
  // plan_39: what is left of your extras is free, so your plan doesn't count on it
  const extraRrsp = 0;
  const extraEf = 0;
  const rrspAll = rrspYear + extraRrsp;
  const efAll = efYear + extraEf;
  const youSave = rrspYear + efYear + youPension * 12;
  const contribYear = rrspAll + pensionYear;
  const setAsideMonthly = (rrspYear + efYear) / 12;

  // Cushion
  const C = plan.cushion;
  // plan_34: fundId 'none' means you unlinked it on purpose, so no fund is
  // matched by its name.
  const efFund = C.fundId === 'none' ? null : W.funds.find((f) => f.id === String(C.fundId)) || W.funds.find((f) => /emergenc/i.test(f.name)) || null;
  const TRF = (plan.travel && plan.travel.fundId) || null;
  const travelFund = TRF === 'none' ? null : (TRF && W.funds.find((f) => f.id === String(TRF))) || W.funds.find((f) => /travel|trip|vacation/i.test(f.name)) || null;
  const efBal = efFund ? efFund.balance : num(C.saved);
  const efTarget = num(C.target);
  const efLeft = Math.max(0, efTarget - efBal);
  const efMonthsNow = emergencyMonth ? efBal / emergencyMonth : 0;
  const efMonthsFull = emergencyMonth ? efTarget / emergencyMonth : 0;
  let efFull = efLeft <= 0 ? 'Full!' : null; // worked out below, month by month

  // Debts
  const debts = W.debts.map((d) => {
    const extra = plan.debts[d.id] || {};
    const guess = isCar(d.name) ? { rate: 6.97, pay: 273, every: 'Every two weeks', ends: '2028-12-14' } : {};
    const x = { ...guess, ...extra };
    const per = x.every === 'Every two weeks' ? 26 / 12 : x.every === 'Every week' ? 52 / 12 : 1;
    return { ...d, rate: num(x.rate), pay: num(x.pay), every: x.every || 'Every month', ends: x.ends || '', monthly: num(x.pay) * per, done: d.original > 0 ? Math.min(100, Math.round((1 - d.balance / d.original) * 100)) : null };
  });

  // Move: your half of the move plus months of living in Europe, from your future fund
  const MV = plan.move;
  const readyY = num(MV.ready) || y + 5, readyM = Math.min(12, Math.max(1, num(MV.readyMonth) || 1));
  const toMove = Math.max(0, (readyY - y) * 12 + (readyM - 1 - now.getMonth()));
  const yMove = toMove / 12;
  const moveHalf = (MV.costs || []).reduce((s, c) => s + num(c.amount), 0);
  const EU = MV.europe || { shared: [], personal: [], months: 6 };
  const euShared = (EU.shared || []).reduce((s, c) => s + num(c.amount), 0);
  const euPersonal = (EU.personal || []).reduce((s, c) => s + num(c.amount), 0);
  const euMonth = euShared / 2 + euPersonal;
  const runwayMonths = num(EU.months) || 6;
  const runwayToday = euMonth * runwayMonths;
  const runway = runwayToday * Math.pow(1 + P, yMove);
  const moveCalc = moveHalf + runway;
  const moveTotal = num(MV.target) || moveCalc; // ln_31: the target you set, or the worked-out one
  const moveThen = moveTotal;
  const futureFund = MV.fundId === 'none' ? null : MV.fundId ? W.funds.find((f) => f.id === String(MV.fundId)) || null : W.funds.find((f) => f !== efFund && /future|moving|move|europe/i.test(f.name)) || null;
  const futureHave = futureFund ? futureFund.balance : num(MV.saved);
  // ln_31: month by month, your goal savings (from your pay, plus the goal half of your extras) fill
  // the goals in the order you lined them up. Once they're all full, it goes to your RRSP.
  const qIds0 = Array.isArray(M.queue) ? M.queue : ['efund', 'move'];
  const qIds = [...qIds0.filter((x) => x !== 'retire'), ...(qIds0.includes('retire') ? ['retire'] : [])]; // ln_33: retirement is always last
  const qb = qIds.map((id) => {
    if (id === 'efund') return { id, e: '🛟', t: efFund ? efFund.name : 'Emergency fund', have: efBal, target: efTarget };
    if (id === 'move') return { id, e: '✈️', t: futureFund ? futureFund.name : 'Future fund', have: futureHave, target: moveTotal };
    if (id === 'baby') {
      const BB = plan.baby;
      if (!BB.on) return null;
      const tu = BB.topUp == null ? Math.round(Math.max(0, payAvg - num(BB.ei))) : num(BB.topUp);
      const gap = Math.max(0, num(BB.months) * payAvg - num(BB.months) * num(BB.ei) - tu * Math.min(num(BB.months), num(BB.topWeeks) * 12 / 52));
      const bf = BB.fundId ? W.funds.find((f) => f.id === String(BB.fundId)) || null : null;
      return { id, e: '\u{1F33F}', t: 'Life events', have: bf ? bf.balance : num(BB.saved), target: num(BB.target) > 0 ? num(BB.target) : gap };
    }
    // ln_33: seeing family, retirement and kids' education can join too
    if (id === 'travel') return { id, e: '🇳🇬', t: 'Seeing family', have: travelFund ? travelFund.balance : 0, target: travelGoal };
    if (id === 'retire') return { id, e: '🌻', t: 'Retirement', have: invested, target: Infinity, keeps: true };
    if (id === 'resp') return plan.resp && plan.resp.on ? { id, e: '🎓', t: "Kids' education", have: 0, target: Infinity, keeps: true } : null;
    const g = (plan.goals || []).find((x) => x.id === id);
    if (!g) return null;
    if (!num(g.amount)) return { id, e: g.e, t: g.t, have: 0, target: 0, needs: true };
    const then = g.year ? num(g.amount) * Math.pow(1 + P, Math.max(0, num(g.year) - y)) : num(g.amount);
    const saved = g.fundId ? ((W.funds.find((f) => f.id === String(g.fundId)) || {}).balance || 0) : num(g.saved);
    return { id, e: g.e, t: g.t, have: saved, target: then };
  }).filter(Boolean);
  // plan_32: your own names and emoji for built-in goals
  const GN = plan.goalNames || {};
  qb.forEach((b) => { const o = GN[b.id]; if (o) { if (o.t) b.t = o.t; if (o.e) b.e = o.e; } });
  const thisMonth = new Date(y, now.getMonth(), 1);
  qb.forEach((b) => { b.bal = b.have; b.fullAt = !b.needs && b.have >= b.target ? thisMonth : null; b.startAt = null; });
  let queueDoneAt = qb.every((b) => b.fullAt || b.needs) ? thisMonth : null;
  const boosts = (Array.isArray(M.boosts) ? M.boosts : []).map((b) => { const [by, bm] = String(b.start || '').split('-').map(Number); return { ...b, at: (by || 9999) * 12 + ((bm || 1) - 1), amt: num(b.amount) }; });
  // plan_21: one-off amounts. Only What if uses these; they're never saved to your plan.
  const firstYm = y * 12 + now.getMonth() + 1;
  const oneOffs = (Array.isArray(M.oneOffs) ? M.oneOffs : []).map((b) => { const [oy, om] = String(b.start || '').split('-').map(Number); return { at: Math.max(firstYm, (oy || 9999) * 12 + ((om || 1) - 1)), amt: num(b.amount) }; });
  // plan_22: What if can start a new goal savings amount from a month you pick
  const efCh = M.efChange ? (() => { const [cy, cm] = String(M.efChange.start || '').split('-').map(Number); return { at: Math.max(firstYm, (cy || 0) * 12 + ((cm || 1) - 1)), a: num(M.efChange.a), b: num(M.efChange.b) }; })() : null;
  const retAdds = [];
  const qMove = qb.find((b) => b.id === 'move');
  let fuAtMove = toMove ? null : futureHave;
  const extraEfMonthly = extraEf / 12;
  const fuSeries = [{ i: 0, v: futureHave }];
  for (let i = 1; i <= 360; i++) {
    const d = new Date(y, now.getMonth() + i, 1);
    const ym = d.getFullYear() * 12 + d.getMonth();
    let add = (d.getMonth() + 1 < step ? num(efCh && ym >= efCh.at ? efCh.a : M.efA) : num(efCh && ym >= efCh.at ? efCh.b : M.efB)) + extraEfMonthly + boosts.reduce((s, b) => s + (ym >= b.at ? b.amt : 0), 0) + oneOffs.reduce((s, b) => s + (ym === b.at ? b.amt : 0), 0);
    for (const b of qb) {
      if (add <= 0) break;
      if (b.needs || b.bal >= b.target - 0.5) continue;
      if (!b.startAt) b.startAt = d;
      const put = Math.min(b.target - b.bal, add);
      b.bal += put; add -= put;
      if (b.id === 'retire') retAdds.push([i, put]);
      if (b.bal >= b.target - 0.5 && !b.fullAt) b.fullAt = d;
    }
    if (add > 0.5 && !queueDoneAt) queueDoneAt = d;
    const fu = qMove ? qMove.bal : futureHave;
    fuSeries.push({ i, v: fu });
    if (i === toMove) fuAtMove = fu;
    if (queueDoneAt && i >= toMove + 3) break;
  }
  const qEf = qb.find((b) => b.id === 'efund');
  const efFullAt = efLeft <= 0 ? thisMonth : qEf ? qEf.fullAt : null;
  const fuFullAt = futureHave >= moveTotal ? thisMonth : qMove ? qMove.fullAt : null;
  // ln_33: when you want each goal full by, and whether the lineup gets it there
  const C0 = plan.cushion || {};
  const byOf = (id) => {
    if (id === 'efund') return num(C0.by) ? new Date(num(C0.by), (num(C0.byMonth) || 12) - 1, 1) : null;
    if (id === 'move') return new Date(readyY, readyM - 1, 1);
    if (id === 'baby') return num(plan.baby.year) ? new Date(num(plan.baby.year), (num(plan.baby.month) || 1) - 1, 1) : null;
    const g = (plan.goals || []).find((x) => x.id === id);
    return g && num(g.year) ? new Date(num(g.year), (num(g.month) || 12) - 1, 1) : null;
  };
  const queue = qb.map(({ id, e, t, have, target, startAt, fullAt, keeps, needs }) => {
    const by = byOf(id);
    const full = !needs && have >= target;
    const st = needs ? 'later' : full ? 'done' : keeps ? (startAt ? 'on' : 'notyet') : by ? (fullAt && fullAt <= by ? 'on' : 'behind') : fullAt ? 'on' : 'notyet';
    return { id, e, t, have, target, startAt, fullAt, keeps: !!keeps, needs: !!needs, by, st };
  });
  if (efLeft > 0 && efFullAt) efFull = monthYear(efFullAt);
  const futureStart = qMove ? (futureHave >= moveTotal ? thisMonth : qMove.startAt) : null;
  const futureMonthly = efAll / 12;
  const readyDate = new Date(readyY, readyM - 1, 1);
  const moveCovered = !!fuFullAt && fuFullAt <= readyDate;
  const futureFull = fuFullAt ? monthYear(fuFullAt) : null;
  const fromInvest = false, useEf = false, moveGross = 0, moveFund = futureFund;
  const moveNeedMonthly = moveCovered ? 0 : Math.max(0, moveTotal - futureHave) / Math.max(1, toMove);

  // Travel this year: what's been used, what's in the fund, what's to come
  const laterFrom = num(TR.laterFrom) || num(MV.ready) || y + 5;
  const travelThis = (yy) => (yy < laterFrom ? travelNowYear : travelLaterYear);
  const travelNominal = (yy) => travelThis(yy) * Math.pow(1 + P, Math.max(0, yy - y));
  const untilAge = num(A.until) || 100;
  const lastYear = y + Math.max(0, untilAge - age);
  const nowYears = Math.max(0, Math.min(laterFrom, lastYear + 1) - y);
  const laterYears = Math.max(0, lastYear + 1 - Math.max(laterFrom, y));
  const travelLifetime = nowYears * travelNowYear + laterYears * travelLaterYear;
  const travelFundBal = travelFund ? travelFund.balance : 0;
  const usedW = travelFund ? (W.fundOut[travelFund.id] || []) : [];
  const usedYou = (TR.used || []).filter((u) => String(u.date || '').slice(0, 4) === String(y));
  const travelUsedList = [...usedW.map((u) => ({ ...u, from: 'w' })), ...usedYou.map((u) => ({ ...u, from: 'y' }))].sort((a, b) => (a.date < b.date ? -1 : 1));
  const travelUsed = travelUsedList.reduce((s, u) => s + num(u.amount), 0);
  const travelHave = travelUsed + travelFundBal;
  const travelToGo = Math.max(0, travelGoal - travelHave);

  // Retirement
  const R = plan.retire;
  const retAge = num(R.age) || 63;
  const yRet = Math.max(0, retAge - age);
  const yearsIn = Math.max(1, untilAge - retAge);
  const spendRet = num(R.everyday) + travelLaterYear;
  const fromSavings = Math.max(0, spendRet - num(R.gov));
  const ann = Math.abs(realRet) < 1e-9 ? yearsIn : (1 - Math.pow(1 + realRet, -yearsIn)) / realRet;
  const retNeedCalc = fromSavings * ann;
  const retNeed = num(R.target) > 0 ? num(R.target) : retNeedCalc; // plan_35
  const firstYearPct = retNeed ? fromSavings / retNeed * 100 : 0;
  const moveHit = fromInvest && yMove <= yRet ? moveGross * Math.pow(1 + real, yRet - yMove) : 0;
  // ln_33: goal savings that reach Retirement in your lineup grow in your RRSP
  const goalsToRet = (nYears) => retAdds.reduce((s, [i, a]) => (i / 12 < nYears ? s + a * Math.pow(1 + real, nYears - i / 12) : s), 0);
  const retFromGoals = goalsToRet(yRet);
  const retHeading = Math.max(0, fv(invested, contribYear, real, yRet) - moveHit) + retFromGoals;
  const annF = Math.abs(real) < 1e-9 ? yRet : (Math.pow(1 + real, yRet) - 1) / real;
  const retNeedYear = yRet > 0 ? Math.max(0, (retNeed + moveHit - invested * Math.pow(1 + real, yRet)) / annF) : 0;
  const retOnTrack = retHeading >= retNeed;
  const retCoveredAge = (() => {
    for (let a = age; a <= 75; a++) {
      const n = a - age, yi = Math.max(1, untilAge - a);
      const an = Math.abs(realRet) < 1e-9 ? yi : (1 - Math.pow(1 + realRet, -yi)) / realRet;
      const hit = fromInvest && yMove <= n ? moveGross * Math.pow(1 + real, n - yMove) : 0;
      const fromGoals = goalsToRet(n);
      if (fv(invested, contribYear, real, n) - hit + fromGoals >= fromSavings * an) return a;
    }
    return null;
  })();

  // Baby and leave
  const B = plan.baby;
  const topUpMonthly = B.topUp == null ? Math.round(Math.max(0, payAvg - num(B.ei))) : num(B.topUp);
  const topMonths = Math.min(num(B.months), num(B.topWeeks) * 12 / 52);
  const topUpTotal = topUpMonthly * topMonths;
  const babyPay = num(B.months) * payAvg;
  const babyEi = num(B.months) * num(B.ei);
  const babyGapCalc = Math.max(0, babyPay - babyEi - topUpTotal);
  const babyGap = num(B.target) > 0 ? num(B.target) : babyGapCalc; // plan_35
  const babyFund = B.fundId ? W.funds.find((f) => f.id === String(B.fundId)) || null : null;
  const babyHave = babyFund ? babyFund.balance : num(B.saved);
  const babyMonthsTo = monthsUntil(B.year, now, B.month);
  const babyNeedMonthly = Math.max(0, babyGap - babyHave) / babyMonthsTo;
  const babyMonthly = num(B.monthly);

  // RESP
  const RS = plan.resp;
  const respGrant = Math.min(500, num(RS.yearly) * 0.2);
  let respTotal = 0, grantSoFar = 0;
  for (let i = 0; i < 18; i++) { const g = Math.min(respGrant, Math.max(0, 7200 - grantSoFar)); grantSoFar += g; respTotal = respTotal * (1 + real) + num(RS.yearly) + g; }

  // Other goals
  const goals = (plan.goals || []).map((g) => {
    const amt = num(g.amount);
    const then = g.year ? amt * Math.pow(1 + P, Math.max(0, num(g.year) - y)) : amt;
    const saved = g.fundId ? ((W.funds.find((f) => f.id === String(g.fundId)) || {}).balance || 0) : num(g.saved);
    const need = amt && g.year ? Math.max(0, then - saved) / monthsUntil(g.year, now, g.month) : 0;
    const qg = queue.find((b) => b.id === g.id);
    return { ...g, then, saved, need, monthly: num(g.monthly), pct: amt ? Math.min(100, Math.round(saved / then * 100)) : null, status: qg ? qg.st : amt ? statusOf(saved, then, num(g.monthly), need) : 'later' };
  });

  // Statuses for the must-haves
  const status = {
    efund: efLeft <= 0 ? 'done' : efAll > 0 ? 'on' : 'notyet',
    travel: extrasIn >= fundsYear ? 'on' : 'behind',
    move: futureHave >= moveTotal ? 'done' : moveCovered ? 'on' : futureMonthly > 0 ? 'behind' : 'notyet',
    baby: B.on ? statusOf(babyHave, babyGap, babyMonthly, babyNeedMonthly) : 'off',
    retire: retOnTrack ? 'on' : 'behind',
  };
  // ln_33: goals in your lineup are on track when the lineup fills them by the date you want
  ['efund', 'move', 'baby'].forEach((k) => { const qg = queue.find((b) => b.id === k); if (qg && status[k] !== 'off') status[k] = qg.st; });

  // Net worth heading for a year (today's dollars)
  const debtPaidBy = (n) => debts.reduce((s, d) => s + Math.min(d.balance, d.monthly * 12 * n), 0);
  const nwAt = (n) => netWorth - invested + fv(invested, contribYear, real, n) + efAll * n + debtPaidBy(n);

  return {
    y, age, P, G, real, realRet,
    netWorth, invested, invW, manInv, fundsCounted, cash: W.cash, assets, debtTotal: debtW + manDebt,
    step, aMonths, bMonths, payA, payB, pay: payAvg, payAvg, essList, essMonth, buffer, emergencyMonth, leftA, leftB,
    rrspYear, efYear, pensionYear, colgateYear, contribYear, setAsideMonthly, youSave,
    extras, extrasIn, sinking, fundsYear, extrasLeft, extraRrsp, extraEf, rrspAll, efAll,
    essentials: emergencyMonth, efFund, efBal, efTarget, efLeft, efMonthsNow, efMonthsFull, efFull,
    debts, carMonthly: debts.reduce((s, d) => s + d.monthly, 0),
    moveTotal, moveThen, yMove, toMove, readyDate, moveHalf, euShared, euPersonal, euMonth, runwayMonths, runwayToday, runway, futureFund, futureHave, efFullAt, futureStart, futureMonthly, fuAtMove, futureFull, moveCovered, fuSeries, moveNeedMonthly, moveFund, fromInvest, useEf, moveGross,
    travelNowYear, travelGoal, travelLaterYear, laterFrom, travelNominal, travelLifetime, travelFund, travelFundBal, nowYears, laterYears, lastYear,
    travelUsedList, travelUsed, travelHave, travelToGo,
    retAge, yRet, yearsIn, spendRet, fromSavings, retNeed, retNeedCalc, firstYearPct, retHeading, retNeedYear, retOnTrack, retCoveredAge, moveHit,
    babyGap, babyGapCalc, babyPay, babyEi, topUpMonthly, topMonths, topUpTotal, babyFund, babyHave, babyMonthsTo, babyNeedMonthly, babyMonthly,
    respGrant, respTotal,
    goals, status, nwAt, queue, queueDoneAt, moveCalc, retFromGoals,
  };
}

// The little things to look at, worked out from your answers.
const MONTHS_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
export function todos(plan, now = new Date(), w = null) {
  const out = [];
  const m = now.getMonth() + 1, yy = now.getFullYear();
  remindersOf(plan).forEach((r) => { // up_03
    const len = Math.round(num(r.for));
    if (!len) { if (!(r.id === 'pension-mix' && plan.invest.pensionMix === 'done')) out.push({ k: r.id, t: r.t, area: r.s || 'Reminder' }); return; }
    const from = Math.min(12, Math.max(1, Math.round(num(r.from)) || 1));
    if ((m - from + 12) % 12 < len) out.push({ k: r.id + '-' + (m >= from ? yy : yy - 1), t: r.t, area: r.s || 'Reminder' });
  });
  const st = Math.round(num(plan.money.stepMonth)) || 8;
  if (plan.baby.on && !plan.baby.fundId) out.push({ k: 'baby-fund', t: 'Make a Life events fund in Worthly', area: 'Goals' });
  if (w && w.efLeft <= 0) out.push({ k: 'ef-full-' + yy, t: 'Your emergency fund is full! Send your ' + fmt(num(plan.money.efA)) + ' a month (' + fmt(num(plan.money.efB)) + ' from ' + MONTHS_S[(st || 8) - 1] + ') to your future fund instead', area: 'Savings' });
  if (w && !w.futureFund) out.push({ k: 'future-fund', t: 'Make a Future Fund in Worthly', area: 'Moving' });
  insOf(plan).forEach((i) => { if (plan.insurance[i.k] === 'Not sure') out.push({ k: 'ins-' + i.k, t: 'Find out about your ' + i.t.toLowerCase() + ' cover', area: 'Insurance' }); });
  accOf(plan).forEach((a) => {
    const v = plan.invest.accounts[a.k];
    if (v === 'To do' && a.k === 'group') out.push({ k: 'acc-group', t: 'Move the work savings plan into your RRSP', area: 'Investing' });
    else if (v === 'Worth a look') out.push({ k: 'acc-' + a.k, t: 'Look into ' + (a.k === 'fhsa' ? 'an FHSA' : a.k === 'rrsp' ? 'an RRSP' : 'a ' + a.t), area: 'Investing' });
  });
  paperOf(plan).forEach((p) => { if (!plan.paper[p.k] && p.k !== 'together') out.push({ k: 'paper-' + p.k, t: p.k === 'will' ? 'Make a will' : p.k === 'benef' ? 'Check your beneficiaries' : p.k === 'where' ? 'Write down where everything lives' : PAPER.some((q) => q.k === p.k) ? 'Set up your ' + p.t.toLowerCase() : p.t, area: 'Paperwork', paper: p.k }); });
  (plan.extraTodos || []).forEach((t) => out.push({ k: t.id, t: t.t, area: 'Your own' }));
  return out;
}

// A snapshot for check-ups and past plans.
export const snapshot = (plan, w, now = new Date()) => ({
  date: now.toISOString(), netWorth: Math.round(w.netWorth), invested: Math.round(w.invested), cushion: Math.round(w.efBal),
  retNeed: Math.round(w.retNeed), retHeading: Math.round(w.retHeading), setAside: Math.round(w.setAsideMonthly),
});

export const checkupDue = (plan, now = new Date()) => {
  if (!plan || !plan.finished) return false;
  const last = plan.checkups && plan.checkups.length ? plan.checkups[plan.checkups.length - 1].date : plan.finishedAt || plan.createdAt;
  const d = new Date(last);
  return (now - d) / 86400000 >= 335;
};
export const nextCheckup = (plan) => {
  const last = plan.checkups && plan.checkups.length ? plan.checkups[plan.checkups.length - 1].date : plan.finishedAt || plan.createdAt;
  const d = new Date(last);
  return monthYear(new Date(d.getFullYear() + 1, d.getMonth(), 1));
};
